import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BookText, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { TrialBalanceTable } from "@/components/journal/TrialBalanceTable";
import { useActiveCompany } from "@/lib/company-context";
import { useAccountBalances } from "@/lib/api/journal.queries";

export const Route = createFileRoute("/companies/$companyId/contabilidad/mayor/")({
  component: LibroMayorPage,
});

function LibroMayorPage() {
  return (
    <RequireAuth>
      <AppShell>
        <LibroMayorContent />
      </AppShell>
    </RequireAuth>
  );
}

function LibroMayorContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [q, setQ] = useState("");

  const { data, isLoading, isError } = useAccountBalances(companyId, {
    periodFrom: periodFrom || undefined,
    periodTo: periodTo || undefined,
  });

  const filteredAccounts = data?.accounts.filter(
    (a) =>
      !q ||
      a.accountCode.includes(q) ||
      a.accountName.toLowerCase().includes(q.toLowerCase()),
  ) ?? [];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <BookText className="h-5 w-5 text-muted-foreground" />
          {t("nav.contabilidad.mayor")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("journal.mayor.subtitle")}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder={t("journal.mayor.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Input
          type="date"
          className="h-9 w-36"
          value={periodFrom}
          onChange={(e) => setPeriodFrom(e.target.value)}
        />
        <Input
          type="date"
          className="h-9 w-36"
          value={periodTo}
          onChange={(e) => setPeriodTo(e.target.value)}
        />
      </div>

      {isError ? (
        <ErrorState title={t("journal.error.loadBalances")} />
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <TrialBalanceTable accounts={filteredAccounts} companyId={companyId} />
      )}
    </div>
  );
}
